import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FilterService } from '@app/core/services/container/filter.service';
import { PermissionService } from '@app/core/services/permission/permission.service';
import { Entity } from '@app/shared/models/models.interface';
import { merge } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseViewComponent } from '../base-view/base-view.component';
import {
  Column,
  ColumnClick,
  COLUMN_ALIGN,
  ContextMenuOption,
  ContextMenuOptionSelected,
  SelectRowClick
} from '../data-table.component';

@Component({
  selector: 'vfs-grid-view',
  templateUrl: './grid-view.component.html',
  styleUrls: ['./grid-view.component.scss']
})
export class GridViewComponent<T extends Entity, S = any> extends BaseViewComponent<T, S>
  implements OnInit, OnChanges, AfterViewInit {
  @Input() allowMultiSelect = false;
  @Output() columnClicked = new EventEmitter<ColumnClick>();
  @Output() selectRowClicked = new EventEmitter<SelectRowClick>();
  @Input() selection: SelectionModel<T> = null;
  @Output() selectionChange = new EventEmitter();
  @Input() multiSelectActive = false;
  @Input() hasHorizontalScroll = false;
  @Input() hasPagination = true;

  @Input() emptyText: string;
  @Input() emptyHint: string;

  // context menu
  @ViewChild(MatMenuTrigger, { static: true }) contextMenu: MatMenuTrigger;
  @Input() contextMenuOptions: Array<ContextMenuOption> = [];
  @Output() contextMenuOptionSelected = new EventEmitter<ContextMenuOptionSelected>();
  contextMenuPosition = { x: '0px', y: '0px' };
  contextMenuDisabledMap = new Map<ContextMenuOption, boolean>();

  changedMultiSelectState = this.multiSelectActive;
  cols = null;

  // checkbox master toogle status
  isMasterToggleIndeterminate = false;
  isMasterToggleActive = false;
  images = {};

  constructor(protected filterService: FilterService, protected permissionService: PermissionService) {
    super(filterService, null);
  }

  /**
   * Get available column names
   */
  getColumnNames(): string[] {
    // This method seems to be called often... so "cache" the columns to improve speed
    if (this.cols == null || this.changedMultiSelectState !== this.multiSelectActive) {
      this.cols = [];

      // Some times the user activates/deactivate the multiselection
      this.changedMultiSelectState = this.multiSelectActive;

      // Recalculate the columns in that case and reset master toggle checkbox
      if (this.allowMultiSelect && this.multiSelectActive) {
        this.isMasterToggleActive = false;
        this.isMasterToggleIndeterminate = false;
        this.cols.push('$$select_all'); // see .html
      }

      // add context menu column
      if (this.hasContextMenu()) {
        this.cols.push('$$ctxmenu'); // see .html
      }

      for (const column of this.columns) {
        this.cols.push(column.name);
      }
    }
    return this.cols;
  }

  /**
   * Initialize the component
   */
  ngOnInit() {
    this.contextMenuOptions.forEach(option => {
      this.contextMenuDisabledMap.set(option, !this.permissionService.hasPermission(option.permissions));
    });
  }

  /**
   * Get column classes
   *
   * @param col column
   * @return the classes
   */
  public getColumnClass(col: Column): string {
    let className = '';
    switch (col.align) {
      case COLUMN_ALIGN.RIGHT:
        className = 'mat-column-align-right';
        break;

      case COLUMN_ALIGN.CENTER:
        className = 'mat-column-align-center';
        break;

      case COLUMN_ALIGN.LEFT:
      default:
        className = 'mat-column-align-left';
        break;
    }

    if (col.action) {
      className += ' vfs-action';
    }

    if (col.isImage) {
      className += ' vfs-table-image';
    }
    return className;
  }

  /**
   * Get the column value
   *
   * @param element the element
   * @param col the column
   * @return the value
   */
  getColValue(element, col: Column) {
    if (!col.name.includes('.')) {
      return element[col.name] != null ? element[col.name] : '';
    }

    let parts = col.name.split('.');
    let currentElement = element;
    for (let i = 0; i < parts.length; ++i) {
      let current = parts[i];
      if (!Array.isArray(currentElement)) {
        currentElement = element[current];
      } else {
        currentElement = currentElement.map(child => child[current]);
      }
    }

    if (!Array.isArray(currentElement)) {
      return currentElement || '';
    } else {
      return currentElement.filter(value => value != null).join(', ');
    }
  }

  /**
   * After view init tasks
   */
  ngAfterViewInit() {
    if (this.paginator) {
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            console.log(this.dataSource.getElements());
            this.loadPage();
          })
        )
        .subscribe();
    } else {
      merge(this.sort.sortChange)
        .pipe(tap(() => this.loadPage()))
        .subscribe();
    }
  }

  /**
   * Click on a cell
   *
   * @param column
   * @param data
   */
  clickOnAction(column, data) {
    this.columnClicked.emit({ column, data });
  }

  /**
   * Select a row
   *
   * @param row the row
   */
  selectRow(row: any) {
    if (this.allowMultiSelect && this.multiSelectActive) {
      this.selection.toggle(row);
      this.selectionChange.emit(this.selection);
      this.setMasterToggleIndeterminate();
      this.checkMasterToggleStatus();
    } else {
      const srClick: SelectRowClick = { row };
      this.selectRowClicked.emit(srClick);
    }
  }

  /**
   * Check if an element is selected
   *
   * @param element the element
   * @return true if so
   */
  isSelected(element) {
    return this.selection.isSelected(element);
  }

  /**
   * Toggle selection (select/unselect items)
   */
  masterToggle() {
    if (this.dataSource) {
      if (this.isMasterToggleActive) {
        this.selection.clear();
        this.selectionChange.emit(this.selection);
        this.isMasterToggleActive = false;
      } else {
        this.dataSource.getElements().forEach(data => this.selection.select(data));
        this.selectionChange.emit(this.selection);
        this.isMasterToggleActive = true;
      }
    }
  }

  /** check if the master toggle is active when an item is selected */
  setMasterToggleIndeterminate() {
    if (this.isMasterToggleActive && !this.isMasterToggleIndeterminate) {
      this.isMasterToggleIndeterminate = true;
    }
  }

  /** check selected items and compare with the total of elements
   * if equal show checked masterToggle
   */
  checkMasterToggleStatus() {
    let totalElements = this.dataSource.getElements().length;
    if (totalElements > 0 && totalElements == this.selection.selected.length) {
      this.isMasterToggleIndeterminate = false;
      this.isMasterToggleActive = true;
    }
    if (this.selection.selected.length == 0) {
      this.isMasterToggleActive = false;
      this.isMasterToggleIndeterminate = false;
    }
  }

  hasContextMenu(): boolean {
    return !this.multiSelectActive && this.contextMenuOptions && this.contextMenuOptions.length > 0;
  }

  onContextMenu(mouseEvent: MouseEvent, menuOption: ContextMenuOption) {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.contextMenuPosition.x = mouseEvent.clientX - 90 + 'px';
    this.contextMenuPosition.y = mouseEvent.clientY + 18 + 'px';
    this.contextMenu.menuData = { item: menuOption };
    this.contextMenu.openMenu();
  }

  /**
   * Emit the option (as ContextMenuOptionSelected object)
   *
   * @param selectedOption the selected option
   */
  onContextMenuAction(selectedOption: ContextMenuOption) {
    const value: ContextMenuOptionSelected = {
      option: selectedOption,
      entity: this.contextMenu.menuData.item
    };

    this.contextMenuOptionSelected.emit(value);
  }
}
